/**
 * Human-readable labels for roadmap UI (language keys, track titles stored as ids, etc.).
 */

/** Shown after the language name in roadmap headers (language overview / track-order scope). */
export const ROADMAP_SCOPE_LABEL = 'Roadmap';

/**
 * Canonical key for matching catalog language fields (Firestore may use mixed case).
 * @param {string|null|undefined} lang
 * @returns {string}
 */
export function normalizeRoadmapLanguageKey(lang) {
  if (lang == null || typeof lang !== 'string') return '';
  const s = lang.trim().toLowerCase();
  const collapsed = s.replace(/\s+/g, '');
  if (s === 'c++' || s === 'c plus plus' || collapsed === 'cplusplus' || collapsed === 'cpp') return 'cpp';
  if (s === 'c#' || s === 'c sharp' || collapsed === 'csharp') return 'csharp';
  if (s === 'js' || s === 'ecmascript') return 'javascript';
  if (s === 'ts') return 'typescript';
  return s;
}

/**
 * Canonical language key from a catalog track (Firestore may use `language`, `lang`, etc.).
 * @param {{ language?: unknown, lang?: unknown, programmingLanguage?: unknown, programming_language?: unknown }|null|undefined} track
 * @returns {string}
 */
export function getTrackLanguageKey(track) {
  if (!track || typeof track !== 'object') return '';
  const raw =
    track.language ??
    track.lang ??
    track.programmingLanguage ??
    track.programming_language;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') return normalizeRoadmapLanguageKey(raw);
  if (typeof raw === 'number') return normalizeRoadmapLanguageKey(String(raw));
  if (typeof raw === 'object') {
    if (typeof raw.language === 'string') return normalizeRoadmapLanguageKey(raw.language);
    if (typeof raw.id === 'string') return normalizeRoadmapLanguageKey(raw.id);
  }
  return normalizeRoadmapLanguageKey(String(raw));
}

/**
 * Whether a published track belongs to a roadmap language row (case-insensitive).
 * @param {{ language?: string|null }} track
 * @param {string} languageKey - normalized or raw catalog key
 */
export function trackMatchesLanguage(track, languageKey) {
  const a = getTrackLanguageKey(track);
  const b = normalizeRoadmapLanguageKey(languageKey);
  return Boolean(a && b && a === b);
}

/** Format track title: split slug separators, title-case (e.g. "Programming_Fundamentals" → "Programming Fundamentals"). */
/**
 * Words that should be rendered uppercase (acronyms / initialisms) when found in track titles.
 * Compared case-insensitively against the cleaned word; case in this set is the canonical render.
 */
const TRACK_TITLE_UPPERCASE_WORDS = new Set([
  'DSA',
  'API',
  'UI',
  'UX',
  'OS',
  'AI',
  'ML',
  'CSS',
  'HTML',
  'SQL',
  'IO',
  'CLI',
  'SDK',
  'JS',
  'TS',
  'AWS',
  'GCP',
  'IOS'
]);

export function formatTrackTitle(title) {
  if (!title) return '';
  return title
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (TRACK_TITLE_UPPERCASE_WORDS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Track title in a language roadmap: drop a leading language word when it matches the catalog
 * language (e.g. "Python Automation Scripting" under Python → "Automation Scripting").
 * @param {string|null|undefined} contextLanguageKey - e.g. URL / menu language
 * @param {string|null|undefined} rawTitle - Firestore/catalog title
 * @param {{ language?: unknown }|null|undefined} track - catalog row (for language match)
 */
export function formatRoadmapTrackTitle(contextLanguageKey, rawTitle, track) {
  const base = formatTrackTitle(rawTitle);
  if (!base) return '';
  if (!track || typeof track !== 'object') return base;
  const ctx = normalizeRoadmapLanguageKey(contextLanguageKey);
  const tlang = getTrackLanguageKey(track);
  if (!ctx || !tlang || ctx !== tlang) return base;
  const label = formatLanguageDisplay(ctx);
  if (!label) return base;
  const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${esc}\\s+`, 'i');
  const rest = base.replace(re, '').trim();
  if (rest.length < 2) return base;
  return rest;
}

/**
 * Display title for a catalog track row. In role / multi-language lists, bare
 * "Programming Fundamentals" is prefixed with the track language (e.g. Python).
 * Under a single-language roadmap, the language prefix is still stripped when redundant.
 *
 * @param {string|null|undefined} contextLanguageKey
 * @param {string|null|undefined} rawTitle
 * @param {{ language?: unknown }|null|undefined} track
 */
export function formatCatalogTrackDisplayTitle(contextLanguageKey, rawTitle, track) {
  const formattedRaw = formatTrackTitle(rawTitle);
  if (!formattedRaw) return '';

  const isGenericProgrammingFundamentals = /^programming fundamentals$/i.test(formattedRaw);
  const ctx = normalizeRoadmapLanguageKey(contextLanguageKey);
  const trackLang = getTrackLanguageKey(track);

  if (isGenericProgrammingFundamentals && trackLang && (!ctx || ctx !== trackLang)) {
    return `${formatLanguageDisplay(trackLang)} Programming Fundamentals`;
  }

  return formatRoadmapTrackTitle(contextLanguageKey, rawTitle, track) || formattedRaw;
}

/**
 * Display label for a catalog language key (matches Learn/course header conventions).
 * @param {string} languageKey - e.g. 'python', 'javascript'
 */
export function formatLanguageDisplay(languageKey) {
  if (!languageKey || typeof languageKey !== 'string') return '';
  const k = languageKey.toLowerCase();
  const map = {
    javascript: 'JavaScript',
    python: 'Python',
    java: 'Java',
    cpp: 'C++',
    csharp: 'C#',
    ruby: 'Ruby',
    typescript: 'TypeScript',
    go: 'Go',
    php: 'PHP',
    rust: 'Rust',
    kotlin: 'Kotlin',
    swift: 'Swift'
  };
  if (map[k]) return map[k];
  return languageKey.charAt(0).toUpperCase() + languageKey.slice(1).toLowerCase();
}

/**
 * True when the formatted track/path title matches the language label (avoids "Python · Python").
 * @param {string} languageKey - catalog key, e.g. 'python'
 * @param {string} rawTitle - track or learning path title from Firestore
 */
export function isTitleRedundantWithLanguage(languageKey, rawTitle) {
  if (!languageKey || typeof languageKey !== 'string' || !rawTitle || typeof rawTitle !== 'string') {
    return false;
  }
  const lang = formatLanguageDisplay(languageKey);
  const title = formatTrackTitle(rawTitle);
  const normalize = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  return normalize(lang) === normalize(title);
}
